/**
 * `computeProjection(model) -> Projection`.
 *
 * Pure: no I/O, no dates, no randomness, no `Intl`. The same input gives the
 * same output in the browser — where the editor calls it inside a `useMemo` on
 * every keystroke — and on the server, where it fills the scenario's summary
 * columns on save. One implementation, so the two can never disagree.
 *
 * It is a generalisation of the `computeProjection` in
 * `zms_license_server/web/src/pages/RoiPage.tsx`: three hard-coded packages
 * become N, five hard-coded costs become line items, and subscriptions gain
 * perpetual and hybrid siblings. The behaviour that page had is preserved to the
 * cent — tests/engine/znms-parity.test.ts is the contract.
 *
 * The algorithm, year by year, is documented in docs/04-projection-engine.md;
 * the section numbers in the comments below refer to it.
 */

import type {
  LineItemInput,
  PackageInput,
  PackageYearBreakdown,
  Projection,
  ProjectionOptions,
  ProjectionWarning,
  ScenarioModel,
  YearResult,
} from "./types.js";

/** Counts keyed by package id. Always holds an entry for every package. */
type Counts = Record<string, number>;

interface Cohort {
  /** The year these clients signed. Churn eligibility is a function of it. */
  year: number;
  counts: Counts;
}

function zeroCounts(packages: PackageInput[]): Counts {
  const out: Counts = {};
  for (const pkg of packages) out[pkg.id] = 0;
  return out;
}

/** `Record` access under `noUncheckedIndexedAccess`; an absent id reads as zero. */
function at(counts: Record<string, number>, id: string): number {
  return counts[id] ?? 0;
}

function sum(counts: Record<string, number>): number {
  let total = 0;
  for (const key of Object.keys(counts)) total += at(counts, key);
  return total;
}

function annualFactor(period: PackageInput["recurringPeriod"]): number {
  return period === "MONTHLY" ? 12 : period === "QUARTERLY" ? 4 : 1;
}

function emptyBreakdown(): PackageYearBreakdown {
  return {
    recurringRevenue: 0,
    oneTimeRevenue: 0,
    maintenanceRevenue: 0,
    revenue: 0,
    recurringCost: 0,
    oneTimeCost: 0,
    assetCapex: 0,
    cost: 0,
    net: 0,
  };
}

/**
 * Split `total` across the given ids in proportion to `weights`, keeping the
 * parts whole numbers that still sum to `total` (largest remainder), and never
 * taking more from an id than its weight allows.
 *
 * Carried over unchanged from the ZNMS page beyond being generalised from three
 * fixed keys to an ordered list. `ids` must already be in `sortOrder`, which is
 * what makes the tie-breaking stable rather than dependent on key insertion.
 */
export function largestRemainder(total: number, weights: Counts, ids: string[]): Counts {
  const out: Counts = {};
  for (const id of ids) out[id] = 0;

  let weightSum = 0;
  for (const id of ids) weightSum += at(weights, id);
  if (total <= 0 || weightSum <= 0) return out;

  const fractions: { id: string; frac: number }[] = [];
  let assigned = 0;
  for (const id of ids) {
    const weight = at(weights, id);
    const raw = (total * weight) / weightSum;
    const base = Math.min(weight, Math.floor(raw));
    out[id] = base;
    assigned += base;
    fractions.push({ id, frac: raw - Math.floor(raw) });
  }
  // Stable: equal fractions keep their sortOrder, because sort() is stable and
  // `fractions` was built in id order.
  fractions.sort((a, b) => b.frac - a.frac);

  // Hand out the remainder one at a time, skipping ids already at their weight.
  // The guard bounds the walk when every remaining id is capped out.
  const rounds = fractions.length * (fractions.length + 1);
  for (let i = 0; assigned < total && i < rounds; i += 1) {
    const entry = fractions[i % fractions.length];
    if (!entry) break;
    if (out[entry.id]! < at(weights, entry.id)) {
      out[entry.id] = at(out, entry.id) + 1;
      assigned += 1;
    }
  }
  return out;
}

/* ------------------------------------------------------------- line items */

function inRange(item: LineItemInput, year: number): boolean {
  if (year < item.startYear) return false;
  return item.endYear === null || year <= item.endYear;
}

/**
 * The client count a line item multiplies: the whole base, or only the packages
 * it names. An item naming a package that no longer exists counts nothing from
 * it, which is what makes deleting a package safe.
 */
function clientsFor(item: LineItemInput, counts: Counts, total: number): number {
  if (item.packageIds.length === 0) return total;
  let restricted = 0;
  for (const id of item.packageIds) restricted += at(counts, id);
  return restricted;
}

/* ------------------------------------------------------------------ engine */

export function computeProjection(
  model: ScenarioModel,
  options: ProjectionOptions = {},
): Projection {
  // sortOrder decides both the reading order of every breakdown and the
  // tie-breaking in largestRemainder, so it is applied once, here.
  const packages = [...model.packages].sort((a, b) => a.sortOrder - b.sortOrder);
  const packageIds = packages.map((pkg) => pkg.id);
  const lineItems = [...model.lineItems].sort((a, b) => a.sortOrder - b.sortOrder);
  const commitmentYears = Math.max(0, Math.round(model.commitmentYears));
  const horizonYears = Math.max(0, Math.round(model.horizonYears));

  // State carried across years.
  const cohorts: Cohort[] = []; // signings that are still paying
  const installed = zeroCounts(packages); // perpetual/hybrid licences ever sold
  const lapsed = zeroCounts(packages); // …of which stopped paying maintenance
  const assetPool = zeroCounts(packages); // units returned by churn, awaiting re-issue

  const rows: YearResult[] = [];
  const discountRate = model.discountRatePct / 100;

  let cumRevenue = 0;
  let cumCost = 0;
  let cumNet = 0;
  let cumDiscountedNet = 0;

  const yearCount = Math.min(horizonYears, model.years.length);
  for (let index = 0; index < yearCount; index += 1) {
    const input = model.years[index]!;
    const year = index + 1;
    const byPackage: Record<string, PackageYearBreakdown> = {};
    for (const id of packageIds) byPackage[id] = emptyBreakdown();

    /* 1. Churn eligibility — cohorts past their commitment. A cohort signed in
       year Y is locked through Y .. Y + commitmentYears - 1. */
    const eligible = zeroCounts(packages);
    for (const cohort of cohorts) {
      if (cohort.year > year - commitmentYears) continue;
      for (const id of packageIds) eligible[id] = at(eligible, id) + at(cohort.counts, id);
    }
    const churnEligible = sum(eligible);
    const churnRequested =
      input.churnMode === "RATE"
        ? Math.max(0, Math.round((churnEligible * input.churnRatePct) / 100))
        : Math.max(0, Math.round(input.churnCount));
    const churnTotal = Math.min(churnRequested, churnEligible);
    const churnByPackage = largestRemainder(churnTotal, eligible, packageIds);

    // Retire the oldest eligible cohort first.
    for (const id of packageIds) {
      let remaining = at(churnByPackage, id);
      for (const cohort of cohorts) {
        if (remaining <= 0) break;
        if (cohort.year > year - commitmentYears) continue;
        const taken = Math.min(remaining, at(cohort.counts, id));
        cohort.counts[id] = at(cohort.counts, id) - taken;
        remaining -= taken;
      }
    }

    /* 2. What churn means depends on the billing model. A perpetual client keeps
       the software and stops paying maintenance; a subscription client leaves
       and, if assets are reused, hands their unit back. */
    const returnedThisYear = zeroCounts(packages);
    for (const pkg of packages) {
      const left = at(churnByPackage, pkg.id);
      if (left === 0) continue;
      if (pkg.billingModel === "SUBSCRIPTION") {
        returnedThisYear[pkg.id] = left;
      } else {
        lapsed[pkg.id] = at(lapsed, pkg.id) + left;
      }
    }

    /* 3. New signings. Only clients assigned to a package earn anything; the
       remainder is reported rather than silently distributed. */
    const newByPackage = zeroCounts(packages);
    for (const id of packageIds) {
      newByPackage[id] = Math.max(0, Math.round(at(input.mix, id)));
    }
    const newTotal = sum(newByPackage);
    const unassigned = Math.max(0, Math.round(input.newClients) - newTotal);
    if (newTotal > 0) cohorts.push({ year, counts: { ...newByPackage } });
    for (const pkg of packages) {
      if (pkg.billingModel === "SUBSCRIPTION") continue;
      installed[pkg.id] = at(installed, pkg.id) + at(newByPackage, pkg.id);
    }

    /* 4. Active base: everyone still paying, after this year's churn and
       signings. For a perpetual package this is installed − lapsed, which is
       what the cohorts now hold. */
    const activeByPackage = zeroCounts(packages);
    for (const cohort of cohorts) {
      for (const id of packageIds) {
        activeByPackage[id] = at(activeByPackage, id) + at(cohort.counts, id);
      }
    }
    const activeTotal = sum(activeByPackage);

    /* 5. Revenue, and 6. cost, per package. */
    let recurringRevenue = 0;
    let oneTimeRevenue = 0;
    let maintenanceRevenue = 0;
    let recurringCost = 0;
    let oneTimeCost = 0;
    let assetCapex = 0;
    let assetsBought = 0;
    let assetsReused = 0;

    for (const pkg of packages) {
      const active = at(activeByPackage, pkg.id);
      const signed = at(newByPackage, pkg.id);
      const periods = annualFactor(pkg.recurringPeriod);
      const line = byPackage[pkg.id]!;

      if (pkg.billingModel !== "PERPETUAL") {
        line.recurringRevenue = active * pkg.recurringPrice * periods;
        line.recurringCost = active * pkg.recurringCost * periods;
      }
      // A perpetual licence is booked in full in the year of signing: this tool
      // models cash position, not revenue recognition. docs/04 §5.
      line.oneTimeRevenue = signed * pkg.oneTimePrice;
      line.oneTimeCost = signed * pkg.oneTimeCost;

      if (pkg.billingModel !== "SUBSCRIPTION" && year >= pkg.maintenanceStartYear) {
        const base =
          pkg.maintenanceMode === "PERCENT_OF_LICENCE"
            ? (pkg.oneTimePrice * pkg.maintenanceRatePct) / 100
            : pkg.maintenanceMode === "FLAT"
              ? pkg.maintenanceFlatAmount
              : 0;
        const eligiblePayers =
          pkg.billingModel === "PERPETUAL" ? at(installed, pkg.id) - at(lapsed, pkg.id) : active;
        line.maintenanceRevenue = eligiblePayers * (pkg.maintenanceAttachPct / 100) * base;
      }

      // Assets: units freed by THIS year's churn are available to THIS year's
      // signings before anything new is bought.
      let buy = signed;
      if (model.reuseAssets) {
        assetPool[pkg.id] = at(assetPool, pkg.id) + at(returnedThisYear, pkg.id);
        const fromPool = Math.min(at(assetPool, pkg.id), signed);
        assetPool[pkg.id] = at(assetPool, pkg.id) - fromPool;
        assetsReused += fromPool;
        buy = signed - fromPool;
      }
      assetsBought += buy;
      line.assetCapex = buy * pkg.assetCost;

      line.revenue = line.recurringRevenue + line.oneTimeRevenue + line.maintenanceRevenue;
      line.cost = line.recurringCost + line.oneTimeCost + line.assetCapex;
      line.net = line.revenue - line.cost;

      recurringRevenue += line.recurringRevenue;
      oneTimeRevenue += line.oneTimeRevenue;
      maintenanceRevenue += line.maintenanceRevenue;
      recurringCost += line.recurringCost;
      oneTimeCost += line.oneTimeCost;
      assetCapex += line.assetCapex;
    }

    /* 7. Line items, in the order docs/04 fixes: everything that is not a
       percentage first, then the percentages against the totals those produced.
       Percentage items therefore never see each other. */
    const byLineItem: Record<string, number> = {};
    let lineItemRevenue = 0;
    let lineItemCost = 0;

    const record = (item: LineItemInput, value: number): void => {
      byLineItem[item.id] = item.direction === "REVENUE" ? value : -value;
      if (item.direction === "REVENUE") lineItemRevenue += value;
      else lineItemCost += value;
    };

    const deferred: LineItemInput[] = [];
    for (const item of lineItems) {
      if (!inRange(item, year)) {
        byLineItem[item.id] = 0;
        continue;
      }
      if (item.basis === "PCT_OF_REVENUE" || item.basis === "PCT_OF_COST") {
        deferred.push(item);
        continue;
      }

      const scale = item.attachPct / 100;
      const activeClients = clientsFor(item, activeByPackage, activeTotal);
      const newClients = clientsFor(item, newByPackage, newTotal);

      let value: number;
      switch (item.basis) {
        case "FIXED_ONE_TIME":
          value = year === item.startYear ? item.amount : 0;
          break;
        case "FIXED_PER_YEAR":
          value = item.amount;
          break;
        case "PER_ACTIVE_CLIENT_YEAR":
          value = item.amount * activeClients * scale;
          break;
        case "PER_NEW_CLIENT_ONCE":
          value = item.amount * newClients * scale;
          break;
        case "PER_ACTIVE_UNIT_YEAR":
          value = item.amount * activeClients * item.unitsPerClient * scale;
          break;
      }
      record(item, value);
    }

    const grossRevenue = recurringRevenue + oneTimeRevenue + maintenanceRevenue + lineItemRevenue;
    const grossCost = recurringCost + oneTimeCost + assetCapex + lineItemCost;
    for (const item of deferred) {
      const base = item.basis === "PCT_OF_REVENUE" ? grossRevenue : grossCost;
      record(item, (item.amount / 100) * base);
    }

    /* 8. Totals. The percentage items have since added to lineItemRevenue and
       lineItemCost, so the year's totals are re-summed rather than taken from
       the gross figures those percentages were measured against. */
    const installedByPackage = { ...installed };
    const yearRevenue = recurringRevenue + oneTimeRevenue + maintenanceRevenue + lineItemRevenue;
    const yearCost = recurringCost + oneTimeCost + assetCapex + lineItemCost;
    const net = yearRevenue - yearCost;

    cumRevenue += yearRevenue;
    cumCost += yearCost;
    cumNet += net;

    const discountFactor = 1 / Math.pow(1 + discountRate, year - 1);
    const discountedNet = net * discountFactor;
    cumDiscountedNet += discountedNet;

    rows.push({
      year,
      newByPackage,
      newTotal,
      unassigned,
      churnByPackage,
      churnTotal,
      churnRequested,
      churnEligible,
      activeByPackage,
      activeTotal,
      installedByPackage,
      installedTotal: sum(installedByPackage),
      recurringRevenue,
      oneTimeRevenue,
      maintenanceRevenue,
      lineItemRevenue,
      revenue: yearRevenue,
      recurringCost,
      oneTimeCost,
      assetCapex,
      assetsBought,
      assetsReused,
      lineItemCost,
      cost: yearCost,
      net,
      cumRevenue,
      cumCost,
      cumNet,
      discountFactor,
      discountedNet,
      cumDiscountedNet,
      byPackage,
      byLineItem,
    });
  }

  // Break-even: the first year cumulative net turns non-negative, interpolated
  // inside that year so the answer is not rounded up to a whole year.
  let breakEvenYears: number | null = null;
  let previousCumNet = 0;
  for (const row of rows) {
    if (breakEvenYears === null && row.cumNet >= 0) {
      const t = row.net !== 0 ? Math.min(1, Math.max(0, -previousCumNet / row.net)) : 0;
      breakEvenYears = row.year - 1 + t;
    }
    previousCumNet = row.cumNet;
  }

  const last = rows[rows.length - 1];
  const nets = rows.map((row) => row.net);

  return {
    rows,
    cumRevenue,
    cumCost,
    cumNet,
    roiPct: cumCost > 0 ? (cumNet / cumCost) * 100 : 0,
    breakEvenYears,
    npv: discountRate === 0 ? null : cumDiscountedNet,
    irrPct: options.includeIrr ? internalRateOfReturn(nets) : null,
    endingActive: last ? last.activeTotal : 0,
    endingInstalled: last ? last.installedTotal : 0,
    endingArr: last ? last.recurringRevenue + last.maintenanceRevenue : 0,
    warnings: collectWarnings(model, packages, rows),
  };
}

/* --------------------------------------------------------------------- IRR */

/**
 * The rate at which the net series discounts to zero, as a percentage, by
 * bisection over [-99%, 1000%]. `null` when the series never changes sign —
 * a model that only ever loses money, or only ever makes it, has no IRR, and
 * the UI hides the tile rather than show a misleading zero.
 */
export function internalRateOfReturn(nets: number[]): number | null {
  if (nets.length === 0) return null;
  const hasPositive = nets.some((net) => net > 0);
  const hasNegative = nets.some((net) => net < 0);
  if (!hasPositive || !hasNegative) return null;

  const npvAt = (rate: number): number =>
    nets.reduce((total, net, index) => total + net / Math.pow(1 + rate, index), 0);

  let low = -0.99;
  let high = 10;
  let npvLow = npvAt(low);
  let npvHigh = npvAt(high);
  // A sign change in the nets does not guarantee one in the NPV over this
  // bracket (a series whose IRR exceeds 1000%, say). Report nothing rather
  // than a bracket endpoint.
  if (npvLow === 0) return low * 100;
  if (npvHigh === 0) return high * 100;
  if (npvLow > 0 === npvHigh > 0) return null;

  for (let i = 0; i < 200 && high - low > 1e-7; i += 1) {
    const mid = (low + high) / 2;
    const npvMid = npvAt(mid);
    if (npvMid === 0) return mid * 100;
    if (npvMid > 0 === npvLow > 0) {
      low = mid;
      npvLow = npvMid;
    } else {
      high = mid;
      npvHigh = npvMid;
    }
  }
  return ((low + high) / 2) * 100;
}

/* ---------------------------------------------------------------- warnings */

function packageEarnsNothing(pkg: PackageInput): boolean {
  const recurring = pkg.billingModel === "PERPETUAL" ? 0 : pkg.recurringPrice;
  const maintenance =
    pkg.billingModel === "SUBSCRIPTION"
      ? 0
      : pkg.maintenanceMode === "PERCENT_OF_LICENCE"
        ? pkg.oneTimePrice * pkg.maintenanceRatePct
        : pkg.maintenanceMode === "FLAT"
          ? pkg.maintenanceFlatAmount
          : 0;
  return recurring <= 0 && pkg.oneTimePrice <= 0 && maintenance <= 0;
}

function collectWarnings(
  model: ScenarioModel,
  packages: PackageInput[],
  rows: YearResult[],
): ProjectionWarning[] {
  const warnings: ProjectionWarning[] = [];

  if (packages.length === 0) {
    warnings.push({
      code: "NO_PACKAGES",
      message: "This scenario has no packages, so nothing can be sold.",
    });
  }

  for (const pkg of packages) {
    if (packageEarnsNothing(pkg)) {
      warnings.push({
        code: "DEAD_PACKAGE",
        packageId: pkg.id,
        message: `“${pkg.name}” has no price its billing model uses, so it earns nothing.`,
      });
    }
    if (pkg.billingModel === "PERPETUAL" && pkg.maintenanceMode === "NONE") {
      warnings.push({
        code: "PERPETUAL_NO_MAINTENANCE",
        packageId: pkg.id,
        message: `“${pkg.name}” sells a perpetual licence with no maintenance, so it earns once and never again.`,
      });
    }
  }

  for (const row of rows) {
    if (row.unassigned > 0) {
      warnings.push({
        code: "UNASSIGNED_CLIENTS",
        year: row.year,
        message: `Year ${row.year}: ${row.unassigned} of ${row.unassigned + row.newTotal} new clients are not assigned to a package and earn nothing.`,
      });
    }
    if (row.churnRequested > row.churnEligible) {
      warnings.push({
        code: "CHURN_CLIPPED",
        year: row.year,
        message:
          model.commitmentYears > 0
            ? `Year ${row.year}: ${row.churnRequested} clients were set to churn but only ${row.churnEligible} are past their ${model.commitmentYears}-year commitment.`
            : `Year ${row.year}: ${row.churnRequested} clients were set to churn but only ${row.churnEligible} are active.`,
      });
    }
  }

  if (rows.length > 0 && rows.every((row) => row.activeTotal === 0 && row.newTotal === 0)) {
    warnings.push({
      code: "EMPTY_HORIZON",
      message: "No clients sign in any year, so nothing here is being sold.",
    });
  }
  // Both can be true at once — a scenario with costs and no clients is empty
  // *and* under water — and each points at a different thing to fix.
  if (rows.length > 0 && rows.every((row) => row.cumNet < 0)) {
    warnings.push({
      code: "NEGATIVE_FOREVER",
      message: `This scenario never breaks even within ${rows.length} years.`,
    });
  }

  return warnings;
}
